import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Handshake, Upload, CheckCircle2, Loader2, ArrowRight, X } from 'lucide-react';
import { PartnerRules, PrivacyPolicyModal } from './ChannelPartnerRules';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: '', email: '', phone: '', address: '',
  state: '', city: '', pincode: '',
  experience_years: 0, license_number: '', message: '',
};

const MAX_PHOTO_MB = 5;

export const JoinAgent = () => {
  const [form, setForm] = useState(INITIAL);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);   // { agent_number, id }
  const [error, setError] = useState('');
  const [policyRead, setPolicyRead] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const onPhoto = (file) => {
    if (!file) return;
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      setError(`Photo exceeds ${MAX_PHOTO_MB} MB limit.`);
      return;
    }
    setError('');
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(form.pincode)) {
      setError('Please enter a valid 6-digit pincode.');
      return;
    }
    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, '').slice(-10))) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!photoFile) {
      setError('Photograph is required for your Agent ID card.');
      return;
    }

    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, String(v ?? '')));
      data.append('origin', window.location.pathname);
      data.append('photo', photoFile);
      const { data: res } = await axios.post(`${API}/agent/apply`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit application. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <section id="join-agent" className="am-reveal py-20 bg-white" data-testid="join-agent-success">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-emerald-50 to-white p-8 rounded-2xl border-2 border-emerald-200 shadow-lg text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Welcome to Acremitra Channel Partners</h2>
            <p className="text-slate-600 mb-6">
              Your application has been received. Our onboarding team will verify your details shortly.
              Save your provisional agent number below for reference.
            </p>
            <div className="bg-white border border-emerald-300 rounded-xl p-5 inline-block mb-6">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Provisional Agent Number</div>
              <div className="font-mono text-2xl font-bold text-slate-900 mt-1" data-testid="agent-number">{result.agent_number}</div>
            </div>
            <div className="text-sm text-slate-500">
              We&apos;ve sent a confirmation to your email. You&apos;ll receive your official joining letter + Agent ID Card
              once Admin verifies your application.
            </div>
            <a
              href={`/track?id=${result.id}&phone=${form.phone.replace(/\D/g, '').slice(-10)}`}
              className="inline-flex items-center gap-2 mt-6 bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 rounded-full font-semibold text-sm"
              data-testid="agent-track-link"
            >
              Track application <ArrowRight size={14} />
            </a>
            <a href="/partner-progress" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline ml-4" data-testid="join-agent-progress-link">
              My partner progress <ArrowRight size={14} />
            </a>
          </div>
          <div className="max-w-5xl mx-auto mt-10">
            <h3 className="text-2xl font-bold text-slate-900 text-center mb-4">Your next steps to become a permanent partner</h3>
            <PartnerRules />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="join-agent" className="am-reveal py-20 bg-white" data-testid="join-agent-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest mb-4">
            <Handshake size={12} /> Channel Partner Program
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-3">Join as a Channel Partner of Acre Mitra</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            To join, there are a few rules to follow. Read them, accept the privacy policy, then fill in your details.
          </p>
        </div>

        <div className="max-w-5xl mx-auto"><PartnerRules /></div>
        <p className="text-center mt-4 text-sm text-slate-600">Already a partner? <a href="/partner-progress" className="font-semibold text-emerald-700 underline" data-testid="partner-progress-link">Track your deals and rewards</a></p>

        <div className="max-w-3xl mx-auto mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center space-y-4" data-testid="partner-consent-card">
          <button type="button" onClick={() => setShowPolicy(true)} className="text-emerald-700 font-semibold underline underline-offset-2" data-testid="partner-read-policy">
            Read the Privacy Policy
          </button>
          <label className={`flex items-center justify-center gap-2 text-sm ${policyRead ? 'text-slate-800 cursor-pointer' : 'text-slate-400'}`}>
            <input type="checkbox" disabled={!policyRead} checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="h-4 w-4 accent-emerald-700" data-testid="partner-policy-checkbox" />
            I have read and agree to the Privacy Policy and the partner rules above
          </label>
          {!policyRead && <p className="text-xs text-slate-500">Open the privacy policy first to unlock the checkbox.</p>}
          <button type="button" disabled={!agreed} onClick={() => setShowForm(true)} className="btn-am-primary w-full sm:w-auto px-8 py-3 disabled:opacity-50" data-testid="partner-start-application">
            Continue to application <ArrowRight size={16} />
          </button>
        </div>
        <PrivacyPolicyModal open={showPolicy} onClose={() => { setShowPolicy(false); setPolicyRead(true); }} />
      </div>

      {showForm && createPortal(
        <div className="fixed inset-0 z-[1000] bg-slate-900/60 grid place-items-center p-4" data-testid="partner-form-modal">
        <div className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-slate-50 p-6 sm:p-8 rounded-2xl shadow-2xl border border-slate-200">
          <button type="button" onClick={() => setShowForm(false)} className="absolute right-4 top-4 text-slate-400 hover:text-slate-700" data-testid="partner-form-close"><X size={18} /></button>
          <h3 className="text-xl font-bold text-slate-900 mb-4">Your details</h3>
          <form onSubmit={submit} className="space-y-5">
            {/* Photo */}
            <div className="flex items-center gap-5 pb-5 border-b border-slate-200">
              <div className="relative">
                <div className={`w-24 h-24 rounded-full overflow-hidden border-2 flex items-center justify-center bg-white ${photoPreview ? 'border-emerald-400' : 'border-dashed border-slate-300'}`}>
                  {photoPreview ? (
                    <img src={photoPreview} alt="applicant" className="w-full h-full object-cover" />
                  ) : (
                    <Upload className="w-8 h-8 text-slate-300" />
                  )}
                </div>
              </div>
              <div className="flex-1">
                <Label className="font-semibold text-slate-800">Passport-size photograph *</Label>
                <p className="text-xs text-slate-500 mb-2">JPG / PNG / WebP · Max 5 MB · Used on your Agent ID card.</p>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onPhoto(e.target.files?.[0])}
                  className="cursor-pointer"
                  data-testid="agent-photo"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="agent-name" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="agent-email" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Phone (10 digits) *</Label>
                <Input required inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} data-testid="agent-phone" />
              </div>
              <div>
                <Label>Experience (Years)</Label>
                <Input type="number" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: parseInt(e.target.value, 10) || 0 })} data-testid="agent-experience" />
              </div>
            </div>

            <div>
              <Label>Address *</Label>
              <Textarea required rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="agent-address" />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>State *</Label>
                <Input required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="agent-state" />
              </div>
              <div>
                <Label>City *</Label>
                <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="agent-city" />
              </div>
              <div>
                <Label>Pincode (6 digits) *</Label>
                <Input required inputMode="numeric" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })} data-testid="agent-pincode" />
              </div>
            </div>

            <div>
              <Label>License / RERA Number (Optional)</Label>
              <Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} data-testid="agent-license" />
            </div>

            <div>
              <Label>Message / Reason to join (Optional)</Label>
              <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="agent-message" />
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5" data-testid="agent-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-am-primary w-full py-4 text-base"
              data-testid="agent-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Submitting…</span>
                </>
              ) : (
                <>
                  <span>Submit Application & Get Agent Number</span>
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        </div>
        </div>,
        document.body,
      )}
    </section>
  );
};

export default JoinAgent;
